import { useIntl } from '@/intl'
import {
  type GroupResponsibleStore,
  type ParticipantFilterType,
  ParticipantFilterValues,
} from '@/scopes/account/group-responsible/domain/types'
import { useGroupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/useGroupResponsibleStore'
import { Label, Sheet, SheetTrigger, tokPx } from '@sunwebgroup/ui-components'
import { Typography } from '@sunwebgroup/ui-components/uds'
import { identity } from 'ramda'
import * as React from 'react'
import { css, styled } from 'styled-components'
import { useStore } from 'zustand'
import { useGroupResponsibleDependencies } from '../dependencies'
import { getFilteredParticipants, getParticipantCounts } from '../selectors'
import { FilterToggleGroup } from './FilterToggleGroup'
import { ParticipantExtraInfoSheetPortal } from './ParticipantExtraInfoSheetPortal'
import {
  ChevronCell,
  EmptyTableMessage,
  Subtitle,
  Table,
  TableCell,
  TableCellWithSubtitle,
  TableHead,
  TableRow,
} from './Table'
import { TableSearch } from './TableSearch'

const breakpointHideCol = 'primitives_breakpoint_m_max'

const sharedBreakpointStyles = css`
  @media (max-width: ${tokPx(breakpointHideCol)}) {
    &:nth-child(2) {
      text-align: right;
    }
  }
`

const TableCellStyledOnBreakpoint = styled(TableCell)`
  ${sharedBreakpointStyles}
`

const TableHeadStyledOnBreakpoint = styled(TableHead)`
  ${sharedBreakpointStyles}
`

export function ParticipantTable() {
  const [filter, setFilter] = React.useState<ParticipantFilterType>('all')
  const [searchTerm, setSearchTerm] = React.useState('')
  const counts = useGroupResponsibleStore(getParticipantCounts)
  const filteredParticipants = useGroupResponsibleStore(state => getFilteredParticipants(state, filter, searchTerm))
  const { groupResponsibleStore } = useGroupResponsibleDependencies()
  const store = useStore(groupResponsibleStore, identity<GroupResponsibleStore>)
  const intl = useIntl()
  type CountKeys = keyof ReturnType<typeof getParticipantCounts>
  const filters: { value: ParticipantFilterType, label: string, countKey: CountKeys }[] = [
    { value: ParticipantFilterValues.ALL, label: 'groups_responsible_all', countKey: 'all' },
    { value: ParticipantFilterValues.NOT_PAID, label: 'groups_responsible_not_paid', countKey: 'notPaid' },
    { value: ParticipantFilterValues.PARTIAL_PAID, label: 'groups_responsible_partial_paid', countKey: 'partialPaid' },
    { value: ParticipantFilterValues.PAID, label: 'groups_responsible_paid', countKey: 'paid' },
  ]

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value)
  }

  return (
    <>
      <TableSearch searchTerm={searchTerm} onSearchChange={handleSearchChange} />
      <FilterToggleGroup filter={filter} filters={filters} counts={counts} onFilterChange={setFilter} />
      {filteredParticipants.length === 0
        ? (
            <EmptyTableMessage />
          )
        : (
            <Sheet modal>
              <Table>
                <thead>
                  <TableRow>
                    <TableHead>
                      <Typography $variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_table_name_label' })}
                      </Typography>
                    </TableHead>
                    <TableHeadStyledOnBreakpoint>
                      <Typography $variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_table_status_label' })}
                      </Typography>
                    </TableHeadStyledOnBreakpoint>
                    <TableHead $hideOn={breakpointHideCol}>
                      <Typography $variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_room' })}
                      </Typography>
                    </TableHead>
                    <TableHead $hideOn={breakpointHideCol}>
                      <Typography $variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_table_amount_due_label' })}
                      </Typography>
                    </TableHead>
                    <TableHead $hideOn={breakpointHideCol} />
                  </TableRow>
                </thead>
                <tbody>
                  {filteredParticipants.map(participant => (
                    <SheetTrigger
                      asChild
                      key={participant.id}
                      onClick={() => {
                        store.changeExtraInfoId(participant.id)
                      }}
                    >
                      <TableRow>
                        <TableCellWithSubtitle>
                          <div>{participant.name}</div>
                          <Subtitle>{participant.email}</Subtitle>
                        </TableCellWithSubtitle>
                        <TableCellStyledOnBreakpoint>
                          <Label $state={participant.status.type}>
                            {intl.formatMessage({ id: participant.status.text })}
                          </Label>
                        </TableCellStyledOnBreakpoint>
                        <TableCell $hideOn={breakpointHideCol}>{intl.formatMessage({ id: participant.room })}</TableCell>
                        <TableCell $hideOn={breakpointHideCol}>{intl.formatCurrency(participant.amountDue)}</TableCell>
                        <ChevronCell $hideOn={breakpointHideCol} />
                      </TableRow>
                    </SheetTrigger>
                  ))}
                </tbody>
              </Table>
              <ParticipantExtraInfoSheetPortal />
            </Sheet>
          )}
    </>
  )
}
